/**
 * DualAxisNutrientChart - Generic dual-axis chart for nutrient parameters
 *
 * Displays two parameters on independent Y-axes (left/right) with time on X-axis
 * Supports both Mastertronic (automated) and Aquarimate (manual) data sources
 * Data grouped by hour for cleaner visualization and tooltips
 */

import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';

import { Paper, Alert, Text, Group } from '@mantine/core';
import { useWaterTestData } from '@/hooks/useWaterTestData';
import { measurePerformance } from '@/utils/performance';
import { calculateTargetRangeAxis } from '@/utils/formatters';
import { useTimeAxis } from '@/hooks/useTimeAxis';
import { memo, useEffect, useMemo, useRef } from 'react';
import { WaterTestPoint } from '@/types/api';
import { useTargetRanges } from '@/hooks/useTargetRanges';
import { TargetRange } from '@/config/targetRanges';
import { ChartSkeleton } from '@/components/charts/ChartSkeleton';
import { ChartHeader } from '@/components/charts/ChartHeader';
import { CHART_GRID_STROKE, CHART_AXIS_TICK, CHART_LEGEND_STYLE } from '@/utils/chartStyles';
import { CHART_CROSSHAIR_COLOR } from '@/utils/chartColors';
import { ChartTooltipContent } from '@/components/charts/ChartTooltipContent';
import {
  ChartAnnotationLayer,
  filterAnnotationsForChart,
} from '@/components/charts/ChartAnnotationLayer';
import type { ChartAnnotation } from '@/types/chartAnnotation';
import { useChartHeight } from '@/hooks/useChartHeight';
import { useIsMobile } from '@/hooks/useIsMobile';

interface ParamConfig {
  name: string;           // 'CA', 'MG', 'NO3', 'PO4'
  label: string;          // 'Calcium', 'Magnesium', etc.
  unit: string;           // 'ppm'
  color: string;          // Dark color for Mastertronic
  lightColor: string;     // Light color for Aquarimate
  rangeKey?: string;      // Key in TARGET_RANGES (lowercase)
}

interface DualAxisNutrientChartProps {
  startDate: Date;
  endDate: Date;
  leftParam: ParamConfig;
  rightParam: ParamConfig;
  /** Pre-fetched water test data for both parameters */
  waterTestData?: Array<{ timestamp: string; param_name: string; value: number; device: string; note?: string }>;
  /** Pre-fetched target ranges */
  targetRangesData?: Array<{ parameterId: string; minValue: number | null; maxValue: number | null; unit: string }>;
  isLoading?: boolean;
  error?: Error | null;
  expanded?: boolean;
  onToggle?: () => void;
  /** Annotations to display on the chart */
  annotations?: ChartAnnotation[];
  /** Callback when the "Add note" button is clicked for the left parameter */
  onAddNoteLeft?: () => void;
  /** Callback when the "Add note" button is clicked for the right parameter */
  onAddNoteRight?: () => void;
  /** Callback when an annotation is clicked for editing */
  onAnnotationClick?: (annotation: ChartAnnotation) => void;
}

interface ChartDataPoint {
  timestamp: number;
  [key: string]: number | string | undefined;
}

/**
 * Groups water test data by hour timestamp
 * Creates flat structure with keys like: ca_mastertronic, mg_aquarimate, mg_manual
 */
function groupDataByHour(data: WaterTestPoint[]): ChartDataPoint[] {
  if (data.length === 0) {
    return [];
  }

  const grouped = new Map<number, ChartDataPoint>();

  data.forEach((point: WaterTestPoint) => {
    const date = new Date(point.timestamp);

    // Validate date
    if (isNaN(date.getTime())) {
      console.warn(`Skipping invalid timestamp: ${point.timestamp}`);
      return;
    }

    // Round to hour (zero out minutes, seconds, milliseconds)
    const roundedTimestamp = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      0,
      0,
      0
    ).getTime();

    // Get or create entry for this hour
    if (!grouped.has(roundedTimestamp)) {
      grouped.set(roundedTimestamp, { timestamp: roundedTimestamp });
    }

    const entry = grouped.get(roundedTimestamp);
    if (!entry) return;

    // Create flat key: param_device (both lowercase)
    // Preserve actual device source including 'manual' for manual entries
    const paramKey = point.param_name.toLowerCase();
    const deviceKey = point.device.toLowerCase();
    const flatKey = `${paramKey}_${deviceKey}`;

    // Add value to entry
    entry[flatKey] = point.value;
    if (point.note) {
      entry[`${flatKey}_note`] = point.note;
    }
  });

  // Convert Map to array and sort by timestamp ascending
  return Array.from(grouped.values()).sort((a, b) => a.timestamp - b.timestamp);
}

export function DualAxisNutrientChart({
  startDate,
  endDate,
  leftParam,
  rightParam,
  waterTestData,
  targetRangesData,
  isLoading: parentIsLoading,
  error: parentError,
  expanded,
  onToggle,
  annotations,
  onAddNoteLeft,
  onAddNoteRight: _onAddNoteRight,  // Reserved for future: separate button for right param
  onAnnotationClick,
}: DualAxisNutrientChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);

  // Responsive hooks
  const chartHeight = useChartHeight();
  const isMobile = useIsMobile();

  // Determine if we should use parent data or fetch our own
  // Also check if parent is loading - if so, wait for parent data instead of fetching
  const useParentData = waterTestData !== undefined || parentIsLoading === true;
  const useParentRanges = targetRangesData !== undefined || parentIsLoading === true;

  // Fetch data for left parameter (e.g., Calcium) - fallback when no parent data
  const {
    data: leftParamData,
    isLoading: leftParamLoading,
    error: leftParamError
  } = useWaterTestData({
    startDate,
    endDate,
    paramName: leftParam.name,
    enabled: !useParentData,
  });

  // Fetch data for right parameter (e.g., Magnesium) - fallback when no parent data
  const {
    data: rightParamData,
    isLoading: rightParamLoading,
    error: rightParamError
  } = useWaterTestData({
    startDate,
    endDate,
    paramName: rightParam.name,
    enabled: !useParentData,
  });

  // Fetch target ranges from API (tank-specific) - fallback when no parent data
  const { data: rangesData } = useTargetRanges({ enabled: !useParentRanges });

  // Convert API ranges to lookup map - prefer parent data if provided
  const targetRanges = useMemo(() => {
    const ranges: Record<string, TargetRange> = {};

    // Use parent data if provided, otherwise use fetched data
    const rangeSource = targetRangesData || rangesData?.ranges;

    if (rangeSource) {
      for (const range of rangeSource) {
        // Only include enabled ranges with both min and max values
        if (range.minValue !== null && range.maxValue !== null) {
          ranges[range.parameterId] = {
            min: range.minValue,
            max: range.maxValue,
            unit: range.unit,
            precision: 2, // Default precision for charts
          };
        }
      }
    }
    return ranges;
  }, [targetRangesData, rangesData]);

  // Combine loading and error states - prefer parent state if using parent data
  const isLoading = useParentData
    ? (parentIsLoading ?? false)
    : (leftParamLoading || rightParamLoading);
  const error = useParentData
    ? (parentError ?? null)
    : (leftParamError || rightParamError);

  // Transform and group data - filter parent data for relevant params if provided
  const chartData = useMemo(() => {
    if (useParentData) {
      // Filter parent data for left and right params
      if (!waterTestData || waterTestData.length === 0) return null;

      const relevantParams = [leftParam.name.toUpperCase(), rightParam.name.toUpperCase()];
      const filteredData = waterTestData.filter(
        (point) => relevantParams.includes(point.param_name.toUpperCase())
      );

      if (filteredData.length === 0) return null;

      // Cast to WaterTestPoint since structure is compatible
      return groupDataByHour(filteredData as WaterTestPoint[]);
    }

    // Fallback: use individually fetched data
    if (!leftParamData && !rightParamData) return null;

    const combinedData: WaterTestPoint[] = [
      ...(leftParamData?.data || []),
      ...(rightParamData?.data || []),
    ];

    if (combinedData.length === 0) return null;

    return groupDataByHour(combinedData);
  }, [useParentData, waterTestData, leftParam.name, rightParam.name, leftParamData, rightParamData]);

  // Range-aware X-axis: dates for multi-day, times for single-day
  const timeAxis = useTimeAxis(startDate, endDate);

  // Calculate Y-axis config for left parameter
  // No hardcoded defaults - axis is derived entirely from target ranges + data
  const leftAxisConfig = useMemo(() => {
    const leftMastKey = `${leftParam.name.toLowerCase()}_mastertronic`;
    const leftAqKey = `${leftParam.name.toLowerCase()}_aquarimate`;
    const leftManKey = `${leftParam.name.toLowerCase()}_manual`;

    const values: number[] = [];
    if (chartData) {
      chartData.forEach((d) => {
        if (d[leftMastKey] != null) values.push(d[leftMastKey] as number);
        if (d[leftAqKey] != null) values.push(d[leftAqKey] as number);
        if (d[leftManKey] != null) values.push(d[leftManKey] as number);
      });
    }

    const hasValues = values.length > 0;
    const rangeKey = leftParam.rangeKey || leftParam.name.toLowerCase();
    const targetRange = targetRanges[rangeKey];

    return calculateTargetRangeAxis({
      dataMin: hasValues ? Math.min(...values) : undefined,
      dataMax: hasValues ? Math.max(...values) : undefined,
      targetMin: targetRange?.min,
      targetMax: targetRange?.max,
    });
  }, [chartData, leftParam.name, leftParam.rangeKey, targetRanges]);

  // Calculate Y-axis config for right parameter
  // No hardcoded defaults - axis is derived entirely from target ranges + data
  const rightAxisConfig = useMemo(() => {
    const rightMastKey = `${rightParam.name.toLowerCase()}_mastertronic`;
    const rightAqKey = `${rightParam.name.toLowerCase()}_aquarimate`;
    const rightManKey = `${rightParam.name.toLowerCase()}_manual`;

    const values: number[] = [];
    if (chartData) {
      chartData.forEach((d) => {
        if (d[rightMastKey] != null) values.push(d[rightMastKey] as number);
        if (d[rightAqKey] != null) values.push(d[rightAqKey] as number);
        if (d[rightManKey] != null) values.push(d[rightManKey] as number);
      });
    }

    const hasValues = values.length > 0;
    const rangeKey = rightParam.rangeKey || rightParam.name.toLowerCase();
    const targetRange = targetRanges[rangeKey];

    return calculateTargetRangeAxis({
      dataMin: hasValues ? Math.min(...values) : undefined,
      dataMax: hasValues ? Math.max(...values) : undefined,
      targetMin: targetRange?.min,
      targetMax: targetRange?.max,
    });
  }, [chartData, rightParam.name, rightParam.rangeKey, targetRanges]);

  // Measure chart render performance
  useEffect(() => {
    if (chartData && !isLoading) {
      const measure = measurePerformance(`DualAxisNutrientChart-${leftParam.name}+${rightParam.name}`);
      measure.end();
    }
  }, [chartData, isLoading, leftParam.name, rightParam.name]);

  // Loading state
  if (isLoading) {
    return <ChartSkeleton title={`${leftParam.label} + ${rightParam.label}`} />;
  }

  // Error handling
  if (error) {
    return (
      <Alert color="red" title={`Error loading ${leftParam.label}/${rightParam.label} data`}>
        {error.message}
      </Alert>
    );
  }

  // No data available
  if (!chartData || chartData.length === 0 || leftAxisConfig === null || rightAxisConfig === null) {
    return (
      <Alert color="blue" title="No data available">
        No {leftParam.label} or {rightParam.label} data found for the selected date range.
      </Alert>
    );
  }

  // Generate data keys for series
  const leftMastertronicKey = `${leftParam.name.toLowerCase()}_mastertronic`;
  const leftAquarimateKey = `${leftParam.name.toLowerCase()}_aquarimate`;
  const leftManualKey = `${leftParam.name.toLowerCase()}_manual`;
  const rightMastertronicKey = `${rightParam.name.toLowerCase()}_mastertronic`;
  const rightAquarimateKey = `${rightParam.name.toLowerCase()}_aquarimate`;
  const rightManualKey = `${rightParam.name.toLowerCase()}_manual`;

  // Detect which data sources have data for conditional legend rendering
  const hasLeftMastertronic = chartData.some((d) => d[leftMastertronicKey] != null);
  const hasLeftAquarimate = chartData.some((d) => d[leftAquarimateKey] != null);
  const hasLeftManual = chartData.some((d) => d[leftManualKey] != null);
  const hasRightMastertronic = chartData.some((d) => d[rightMastertronicKey] != null);
  const hasRightAquarimate = chartData.some((d) => d[rightAquarimateKey] != null);
  const hasRightManual = chartData.some((d) => d[rightManualKey] != null);

  // Get sources that have data for annotation filtering
  const activeSources = [
    hasLeftMastertronic || hasRightMastertronic ? 'mastertronic' : null,
    hasLeftAquarimate || hasRightAquarimate ? 'aquarimate' : null,
    hasLeftManual || hasRightManual ? 'manual' : null,
  ].filter(Boolean) as string[];

  return (
    <Paper ref={chartRef} p={{ base: 'sm', md: 'lg' }} withBorder radius={12} bg="var(--rdh-card-bg)">
      <ChartHeader
        title={`${leftParam.label} + ${rightParam.label}`}
        subtitle={isMobile ? `(${leftParam.unit})` : undefined}
        chartRef={chartRef}
        expanded={expanded}
        onToggle={onToggle}
        onAddNote={onAddNoteLeft}
      />

      {/* Unit labels above axes on mobile */}
      {isMobile && (
        <Group justify="space-between" mb="xs">
          <Text size="xs" c="dimmed">{leftParam.unit}</Text>
          <Text size="xs" c="dimmed">{rightParam.unit}</Text>
        </Group>
      )}

      <ResponsiveContainer width="100%" height={chartHeight}>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />

          {/* Annotation regions for left parameter */}
          {annotations && leftParam.rangeKey && (
            <ChartAnnotationLayer
              annotations={filterAnnotationsForChart(
                annotations,
                leftParam.rangeKey,
                activeSources
              )}
              onAnnotationClick={onAnnotationClick}
            />
          )}

          {/* Annotation regions for right parameter */}
          {annotations && rightParam.rangeKey && (
            <ChartAnnotationLayer
              annotations={filterAnnotationsForChart(
                annotations,
                rightParam.rangeKey,
                activeSources
              )}
              onAnnotationClick={onAnnotationClick}
            />
          )}

          {/* X-axis: Time */}
          <XAxis
            dataKey="timestamp"
            type="number"
            domain={[startDate.getTime(), new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() + 1).getTime()]}
            ticks={timeAxis.ticks}
            tickFormatter={timeAxis.formatTick}
            tick={CHART_AXIS_TICK}
            axisLine={false}
            tickLine={false}
            height={isMobile ? 35 : 50}
          />

          {/* Left Y-axis for left parameter (e.g., Calcium) */}
          <YAxis
            yAxisId="left"
            orientation="left"
            width={isMobile ? 40 : 60}
            tick={CHART_AXIS_TICK}
            axisLine={false}
            tickLine={false}
            domain={leftAxisConfig.domain}
            ticks={leftAxisConfig.ticks}
            tickFormatter={leftAxisConfig.tickFormatter}
          />

          {/* Right Y-axis for right parameter (e.g., Magnesium) */}
          <YAxis
            yAxisId="right"
            orientation="right"
            width={isMobile ? 40 : 60}
            tick={CHART_AXIS_TICK}
            axisLine={false}
            tickLine={false}
            domain={rightAxisConfig.domain}
            ticks={rightAxisConfig.ticks}
            tickFormatter={rightAxisConfig.tickFormatter}
          />

          {/* Crosshair tooltip with custom content */}
          <Tooltip
            cursor={{
              stroke: CHART_CROSSHAIR_COLOR,
              strokeWidth: 1,
              strokeDasharray: '4 4',
            }}
            content={
              <ChartTooltipContent
                valueFormatter={(v, name) => {
                  const decimals = name.toLowerCase().includes('ca_') || name.toLowerCase().includes('mg_') ? 0 : 2;
                  return `${v.toFixed(decimals)} ${name.toLowerCase().startsWith(leftParam.name.toLowerCase()) ? leftParam.unit : rightParam.unit}`;
                }}
                nameFormatter={formatSeriesName}
              />
            }
          />

          <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={CHART_LEGEND_STYLE} formatter={(value) => formatSeriesName(value)} />

          {/* Left param - Mastertronic (dark color, line) - only show if has data */}
          {hasLeftMastertronic && (
            <Line
              yAxisId="left"
              type="monotone"
              dataKey={leftMastertronicKey}
              stroke={leftParam.color}
              strokeWidth={2}
              dot={{ r: 3, fill: leftParam.color }}
              name={leftMastertronicKey}
              activeDot={{ r: 6 }}
              connectNulls={true}
              isAnimationActive={false}
            />
          )}

          {/* Left param - Aquarimate (light color, triangles with line) - only show if has data */}
          {hasLeftAquarimate && (
            <Line
              yAxisId="left"
              type="monotone"
              dataKey={leftAquarimateKey}
              stroke={leftParam.lightColor}
              strokeWidth={1.5}
              dot={<TriangleMarker fill={leftParam.lightColor} />}
              activeDot={{ r: 6 }}
              name={leftAquarimateKey}
              connectNulls={true}
              isAnimationActive={false}
            />
          )}

          {/* Right param - Mastertronic (dark color, line) - only show if has data */}
          {hasRightMastertronic && (
            <Line
              yAxisId="right"
              type="monotone"
              dataKey={rightMastertronicKey}
              stroke={rightParam.color}
              strokeWidth={2}
              dot={{ r: 3, fill: rightParam.color }}
              name={rightMastertronicKey}
              activeDot={{ r: 6 }}
              connectNulls={true}
              isAnimationActive={false}
            />
          )}

          {/* Right param - Aquarimate (light color, triangles with line) - only show if has data */}
          {hasRightAquarimate && (
            <Line
              yAxisId="right"
              type="monotone"
              dataKey={rightAquarimateKey}
              stroke={rightParam.lightColor}
              strokeWidth={1.5}
              dot={<TriangleMarker fill={rightParam.lightColor} />}
              activeDot={{ r: 6 }}
              name={rightAquarimateKey}
              connectNulls={true}
              isAnimationActive={false}
            />
          )}

          {/* Left param - Manual entries (diamonds with line) - only show if has data */}
          {hasLeftManual && (
            <Line
              yAxisId="left"
              type="monotone"
              dataKey={leftManualKey}
              stroke={leftParam.color}
              strokeWidth={1.5}
              dot={<DiamondMarker fill={leftParam.color} />}
              activeDot={{ r: 6 }}
              name={leftManualKey}
              connectNulls={true}
              isAnimationActive={false}
            />
          )}

          {/* Right param - Manual entries (diamonds with line) - only show if has data */}
          {hasRightManual && (
            <Line
              yAxisId="right"
              type="monotone"
              dataKey={rightManualKey}
              stroke={rightParam.color}
              strokeWidth={1.5}
              dot={<DiamondMarker fill={rightParam.color} />}
              activeDot={{ r: 6 }}
              name={rightManualKey}
              connectNulls={true}
              isAnimationActive={false}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </Paper>
  );
}

/**
 * Custom triangle marker for Aquarimate data points
 * Used as the dot prop for Line components
 *
 * Memoized to prevent unnecessary re-renders with many data points.
 *
 * Note: Recharts calls dot renderer for every data point in the array,
 * even when this series has no value at that timestamp. We must check
 * `value` to avoid rendering markers for undefined data.
 */
const TriangleMarker = memo(function TriangleMarker(props: any) {
  const { cx, cy, fill, value } = props;

  // Don't render if there's no data value for this series at this timestamp
  if (value == null) return null;

  // Draw a triangle pointing up
  const size = 6;
  const points = `${cx},${cy - size} ${cx - size},${cy + size * 0.6} ${cx + size},${cy + size * 0.6}`;
  return <polygon points={points} fill={fill} />;
});

/**
 * Custom diamond marker for Manual data points
 * Used as the dot prop for Line components
 *
 * Memoized to prevent unnecessary re-renders with many data points.
 *
 * Note: Recharts calls dot renderer for every data point in the array,
 * even when this series has no value at that timestamp. We must check
 * `value` to avoid rendering markers for undefined data.
 */
const DiamondMarker = memo(function DiamondMarker(props: any) {
  const { cx, cy, fill, value } = props;

  // Don't render if there's no data value for this series at this timestamp
  if (value == null) return null;

  // Draw a diamond
  const size = 5;
  const points = `${cx},${cy - size} ${cx + size},${cy} ${cx},${cy + size} ${cx - size},${cy}`;
  return <polygon points={points} fill={fill} />;
});

/**
 * Format series name for display in legend/tooltip
 * Converts: ca_mastertronic -> Ca (Mast)
 *
 * Uses abbreviations for better mobile display:
 * - Ca, Mg, NO3, PO4 instead of full names
 * - Mast, Aq for device names
 *
 * @param name - Series key in format: param_device (e.g., "ca_mastertronic")
 * @returns Formatted display name (e.g., "Ca (Mast)")
 */
function formatSeriesName(name: string): string {
  const parts = name.split('_');
  const param = parts[0] || '';
  const device = parts[1] || '';

  // Use abbreviations instead of full names
  const paramNames: Record<string, string> = {
    ca: 'Ca',
    mg: 'Mg',
    no3: 'NO3',
    po4: 'PO4',
  };

  const deviceNames: Record<string, string> = {
    mastertronic: 'Mast',
    aquarimate: 'Aq',
    manual: 'Manual',
  };

  const paramLabel = paramNames[param.toLowerCase()] || param.toUpperCase();
  const deviceLabel = deviceNames[device.toLowerCase()] || device;

  return `${paramLabel} (${deviceLabel})`;
}
